import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Select, { components } from 'react-select';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import chevronLeft from 'assets/icons/chevron-left.svg';
import { SelectInput, TextInput } from 'components/Inputs';
import Button from 'components/Button';
import getStripe from 'utils/stripe';
import { get, getUserIdFromToken, post } from 'api';
import { checkRegistrationPoints } from 'utils/tournamentHelper';
import { SuccessToast, ErrorToast } from 'utils/Toasts';
import searchLg from 'assets/icons/search-lg.svg';
import SpinningBox from 'components/SpinningBox';

const Registration = () => {
  const theme = useTheme();
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const successToastRef = useRef();
  const errorToastRef = useRef();
  const [profile, setProfile] = useState({});
  const [open, setOpen] = useState({});
  const [tournamentsData, setTournamentsData] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState({
    player1_id: '',
    player1_full_name: '',
    player1_email: '',
    tournament_id: '',
    gender_id: '',
    level_id: '',
    player2_id: '',
    player2_full_name: '',
    player2_email: '',
  });
  const [isNewCustomer, setIsNewCustomer] = useState(null);
  const [tournamentGenders, setTournamentGenders] = useState([]);
  const [tournamentLevels, setTournamentLevels] = useState([]);
  const [players, setPlayers] = useState([]);
  const userId = getUserIdFromToken();
  const [collectPayment, setCollectPayment] = useState(true);

  useEffect(() => {
    if (form.tournament_id) {
      const isCollectingPayment = tournamentsData.find(
        (t) => t.id === form.tournament_id,
      ).collect_payment;
      const collectPayment = isCollectingPayment === 1;
      setCollectPayment(collectPayment);
    }
  }, [form.tournament_id, tournamentsData]);

  const Placeholder = (props) => {
    return (
      <components.Placeholder {...props}>
        <Box display={'flex'} alignItems={'center'}>
          <Box component={'img'} src={searchLg} alt="search" />{' '}
          <Box marginLeft={'8px'}>{props.children}</Box>
        </Box>
      </components.Placeholder>
    );
  };

  const checkAlreadyRegistered = async ({
    tournamentId,
    userId,
    gender,
    level,
  }) => {
    try {
      const response = await get(
        `/tournament-registration/check-registration/${tournamentId}/${userId}/${gender}/${level}`,
      );
      const data = await response.json();
      return data.registered;
    } catch (err) {
      console.error('Failed to check registration status', err);
      return false;
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');

    if (sessionId && form.player1_id && form.tournament_id) {
      setIsSubmitting(true);
      const pollRegistration = async () => {
        let attempts = 0;
        const maxAttempts = 10;

        const interval = setInterval(async () => {
          attempts++;
          const isRegistered = await checkAlreadyRegistered({
            tournamentId: form.tournament_id,
            userId: form.player1_id,
            gender: form.gender_id,
            level: form.level_id,
          });

          if (isRegistered || attempts >= maxAttempts) {
            clearInterval(interval);

            if (isRegistered) {
              successToastRef.current.showSuccess(t('registrationSuccess'));
              setTimeout(() => {
                setIsSubmitting(false);
                localStorage.removeItem('formData');
                navigate(`/turnyrai/${id}`, {
                  state: { from: 'tournament-registration', data: form },
                });
              }, 3000);
            } else {
              errorToastRef.current.showError(t('registrationNotConfirmed'));
            }
          }
        }, 3000);
      };

      pollRegistration();
    }
  }, [location.search, form.player1_id, form.tournament_id]);

  const selectedTournament = tournamentsData.find(
    (t) => t.id === form.tournament_id,
  );

  const filteredGenders = useMemo(() => {
    return tournamentGenders.filter((type) => {
      if (!selectedTournament) return false;

      if (
        type.id === selectedTournament.men_id &&
        selectedTournament.men_enabled
      ) {
        return true;
      }
      if (
        type.id === selectedTournament.women_id &&
        selectedTournament.women_enabled
      ) {
        return true;
      }
      if (
        type.id === selectedTournament.mix_id &&
        selectedTournament.mix_enabled
      ) {
        return true;
      }

      return false;
    });
  }, [form.tournament_id, tournamentsData, tournamentGenders]);

  const filteredLevels = useMemo(() => {
    if (!selectedTournament || !form.gender_id) return [];

    // Determine which levels to use based on the selected gender_id
    let genderLevels = [];
    if (form.gender_id === selectedTournament.men_id) {
      genderLevels = selectedTournament.men_levels || [];
    } else if (form.gender_id === selectedTournament.women_id) {
      genderLevels = selectedTournament.women_levels || [];
    } else if (form.gender_id === selectedTournament.mix_id) {
      genderLevels = selectedTournament.mix_levels || [];
    }

    // Filter levels where registration is open
    return genderLevels.filter((level) => level.is_registration_open);
  }, [form.tournament_id, form.gender_id, tournamentsData]);

  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const response = await get(
          '/tournament-registration/tournament-genders',
        );
        const data = await response.json();
        setTournamentGenders(data);
      } catch (error) {
        console.error('Failed to fetch levels', error);
      }
    };

    fetchTypes();
  }, []);

  useEffect(() => {
    const fetchLevels = async () => {
      if (!form.gender_id) return;
      try {
        const response = await get(
          `/tournament-registration/tournament-levels/${form.gender_id}/${form.tournament_id}`,
        );
        const data = await response.json();
        setTournamentLevels(data);
      } catch (error) {
        console.error('Failed to fetch levels', error);
      }
    };

    fetchLevels();
  }, [form.gender_id, form.tournament_id]);

  useEffect(() => {
    const getUserDetails = async () => {
      const response = await get('/accounts/myprofile');
      const data = await response.json();
      setProfile(data[0]);

      const savedForm = JSON.parse(localStorage.getItem('formData'));
      if (savedForm) {
        setForm({
          ...savedForm,
          player1_id: data[0].id,
          player1_full_name: `${data[0].first_name} ${data[0].last_name}`,
          player1_email: data[0].email,
        });
      } else {
        setForm({
          ...form,
          player1_id: data[0].id,
          player1_full_name: `${data[0].first_name} ${data[0].last_name}`,
          player1_email: data[0].email,
        });
      }
    };

    const getTournaments = async () => {
      const response = await get('/tournament-registration/reg-open');
      const data = await response.json();
      const options = data.map((tournament) => ({
        value: tournament.id,
        label: `${tournament.tournament_name} (${tournament.city})`,
      }));
      setTournaments(options);
      setTournamentsData(data);
    };

    getUserDetails();
    getTournaments();
  }, []);

  useEffect(() => {
    const getPlayers = async () => {
      try {
        const response = await get(`/accounts/usersearch`);

        const data = await response.json();
        const players = data
          .filter((player) => player.id !== userId) // Filter out current user
          .map((player) => {
            return {
              value: player.id,
              label: `${player.first_name} ${player.last_name}`,
            };
          });
        setPlayers(players);
      } catch (err) {
        console.error(err.message);
      }
    };
    getPlayers();
  }, []);

  useEffect(() => {
    const getIsNewCustomer = async () => {
      const response = await get(
        `/tournament-registration/check-new-customer/${userId}`,
      );
      const data = await response.json();
      setIsNewCustomer(data);
    };
    getIsNewCustomer();
  }, [userId]);

  const handleOpen = (id) => {
    setOpen({ ...open, [id]: true });
  };

  const handleClose = (id) => {
    setOpen({ ...open, [id]: false });
  };

  const handleChange = (event) => {
    setForm({
      ...form,
      [event.target.name]: event.target.value,
    });
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const registrationPoints = checkRegistrationPoints(
      tournamentLevels.find((level) => level.id === form.level_id)?.name,
    );
    const tournament = tournamentsData.find((t) => t.id === form.tournament_id);

    if (
      !form.tournament_id ||
      !form.gender_id ||
      !form.level_id ||
      !form.player2_id
    ) {
      errorToastRef.current.showError(t('fillAllFields'));
      setIsSubmitting(false);
      return;
    }

    const isDuplicate = await checkAlreadyRegistered({
      tournamentId: form.tournament_id,
      userId: form.player1_id,
      gender: form.gender_id,
      level: form.level_id,
    });

    if (isDuplicate) {
      errorToastRef.current.showError(t('alreadyRegistered'));
      setIsSubmitting(false);
      return;
    }

    const formToSend = { ...form, registrationPoints };
    localStorage.setItem('formData', JSON.stringify(formToSend));

    if (tournament.collect_payment === 1) {
      try {
        // Save temp registration
        await post('/stripe/temp-save', formToSend);

        // Create Stripe session
        const sessionRes = await post('/stripe/stripe-session', {
          tournament_id: form.tournament_id,
          player1_email: form.player1_email,
          player1_id: form.player1_id,
          player2_id: form.player2_id,
          level_id: form.level_id,
          gender_id: form.gender_id,
          isNewCustomer,
        });
        const sessionData = await sessionRes.json();

        if (!sessionData.id) {
          throw new Error(t('paymentError'));
        }

        const stripe = await getStripe();
        await stripe.redirectToCheckout({ sessionId: sessionData.id });
      } catch (error) {
        console.error('Stripe checkout failed:', error);
        errorToastRef.current.showError(t('paymentError'));
        setIsSubmitting(false);
      }
    } else {
      // Register directly
      await registerForTournament(registrationPoints);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('stripeCanceled') === 'true') {
      if (errorToastRef.current) {
        errorToastRef.current.showError(t('canceledPayment'));
      }
      localStorage.removeItem('formData');
      setIsSubmitting(false);
      setTimeout(() => {
        navigate(`/turnyras/registracija/${id}`);
      }, 3000);
    }
  }, [location.search, id]);

  async function registerForTournament(registrationPoints) {
    const response = await post('/tournament-registration/registration', {
      tournament_id: form.tournament_id,
      player1_id: form.player1_id,
      player1_full_name: form.player1_full_name,
      player1_email: form.player1_email,
      gender_id: form.gender_id,
      level_id: form.level_id,
      player2_id: form.player2_id,
      player2_full_name: form.player2_full_name,
      player2_email: form.player2_email,
      registrationPoints,
    });
    const data = await response.json();
    if (data.error) {
      errorToastRef.current.showError(data.error);
      setIsSubmitting(false);
    } else {
      successToastRef.current.showSuccess(t('registrationSuccess'));
      setTimeout(() => {
        localStorage.removeItem('formData');
        setIsSubmitting(false);
        navigate(`/turnyrai/${id}`, {
          state: { from: 'tournament-registration', data: form },
        });
      }, 3000);
    }
  }

  return (
    <Box
      sx={{
        marginBottom: '100px',
        margin: '0 auto',
        maxWidth: '416px',
        width: '100%',
        height: 'auto',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <SuccessToast ref={successToastRef} />
      <ErrorToast ref={errorToastRef} />
      <Box
        display={'flex'}
        alignItems={'center'}
        onClick={() => navigate(`/turnyras/${id}`)}
        sx={{ cursor: 'pointer' }}
        marginBottom={'32px'}
      >
        <Box
          component={'img'}
          src={chevronLeft}
          marginRight={'12px'}
          height={1}
          alt="chevron Left"
        />
        <Typography
          variant="body3"
          color={theme.palette.darkGrey[700]}
          sx={{ fontWeight: 600 }}
        >
          {t('goBack')}
        </Typography>
      </Box>
      <Typography
        variant="h5"
        fontWeight={700}
        color={theme.palette.darkGrey[700]}
      >
        {t('joinTournament')}
      </Typography>
      <Typography
        variant="body3"
        color={theme.palette.darkGrey[700]}
        sx={{ marginTop: '12px' }}
      >
        {t('tournamentRegSubtitle')}
      </Typography>
      <Box>
        <Box
          display={'flex'}
          flexDirection={'column'}
          maxWidth={{ xs: '100%', sm: '100%' }}
          marginTop={'24px'}
        >
          <TextInput
            label={`${t('firstName')} ${t('lastName')}`}
            id="player1_full_name"
            name="player1_full_name"
            type="text"
            placeholder={`${t('firstName')} ${t('lastName')}`}
            disabled
            handleInputChange={handleChange}
            value={form.player1_full_name}
          />
        </Box>
        <Box
          display={'flex'}
          flexDirection={'column'}
          maxWidth={{ xs: '100%', sm: '100%' }}
          marginTop={'24px'}
        >
          <TextInput
            label={t('email')}
            id="player1_email"
            name="player1_email"
            type="email"
            placeholder={t('email')}
            disabled
            handleInputChange={handleChange}
            value={form.player1_email}
          />
        </Box>
        <Box
          display={'flex'}
          flexDirection={'column'}
          maxWidth={{ xs: '100%', sm: '100%' }}
          marginTop={'24px'}
        >
          <Typography
            variant="body4"
            fontWeight={500}
            color={theme.palette.darkGrey[700]}
          >
            {t('tournament')}*
          </Typography>
          <SelectInput
            name="tournament_id"
            id="tournament_id"
            placeholder={t('selectTournament')}
            open={open}
            handleOpen={handleOpen}
            handleClose={handleClose}
            handleChange={handleChange}
            topMargin={'6px'}
            value={form.tournament_id}
            options={tournaments}
          />
        </Box>
        <Box
          display={'flex'}
          flexDirection={'column'}
          maxWidth={{ xs: '100%', sm: '100%' }}
          marginTop={'24px'}
        >
          <Typography
            variant="body4"
            fontWeight={500}
            color={theme.palette.darkGrey[700]}
          >
            {t('tournamentType')}*
          </Typography>
          <SelectInput
            name="gender_id"
            id="gender_id"
            placeholder={t('selectTournamentType')}
            open={open}
            handleOpen={handleOpen}
            handleClose={handleClose}
            handleChange={handleChange}
            topMargin={'6px'}
            value={form.gender_id}
            options={filteredGenders.map((type) => ({
              value: type.id,
              label: t(`${type.name}`),
            }))}
          />
        </Box>
        <Box
          display={'flex'}
          flexDirection={'column'}
          maxWidth={{ xs: '100%', sm: '100%' }}
          marginTop={'24px'}
        >
          <Typography
            variant="body4"
            fontWeight={500}
            color={theme.palette.darkGrey[700]}
          >
            {t('level')}*
          </Typography>
          <SelectInput
            name="level_id"
            id="level_id"
            placeholder={t('selectLevel')}
            open={open}
            handleOpen={handleOpen}
            handleClose={handleClose}
            handleChange={handleChange}
            topMargin={'6px'}
            value={form.level_id}
            options={filteredLevels.map((level) => ({
              value: level.level_id,
              label: t(`${level.level_name}`),
            }))}
          />
        </Box>
        <Box
          display={'flex'}
          flexDirection={'column'}
          maxWidth={{ xs: '100%', sm: '100%' }}
          marginTop={'24px'}
        >
          <Typography
            variant="body4"
            fontWeight={500}
            color={theme.palette.darkGrey[700]}
          >
            {t('partner')}*
          </Typography>
          <Select
            options={players}
            placeholder={t('selectPartner')}
            isSearchable={true}
            onChange={(selectedOption) => {
              setForm({
                ...form,
                player2_id: selectedOption.value,
                player2_full_name: selectedOption.label,
              });
            }}
            components={{ Placeholder }}
            menuPlacement="top"
            styles={{
              control: (provided, state) => ({
                ...provided,
                boxShadow: 'none',
                borderRadius: '8px',
                border: `1px solid ${theme.palette.darkGrey[300]}`,
                backgroundColor: theme.palette.base.white,
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: 'none',
                },
              }),
              menu: (provided, state) => ({
                ...provided,
                cursor: 'pointer',
                color: theme.palette.darkGrey[600],
                backgroundColor: theme.palette.base.white,
              }),
              option: (provided, state) => ({
                ...provided,
                cursor: 'pointer',
                color: theme.palette.darkGrey[600],
                backgroundColor: theme.palette.base.white,
                fontSize: '16px',
                lineHeight: '24px',
                fontWeight: 400,
                fontFamily: 'Inter, sans-serif',
                '&:hover': {
                  backgroundColor: theme.palette.darkGrey[50],
                  color: theme.palette.darkGrey[600],
                },
              }),
              singleValue: (provided, state) => ({
                ...provided,
                color: theme.palette.darkGrey[600],
                backgroundColor: theme.palette.base.white,
                fontSize: '16px',
                lineHeight: '24px',
                fontWeight: 400,
                fontFamily: 'Inter, sans-serif',
              }),
              placeholder: (provided, state) => ({
                ...provided,
                color: theme.palette.darkGrey[500],
                fontSize: '16px',
                lineHeight: '24px',
                fontWeight: 400,
                fontFamily: 'Inter, sans-serif',
              }),
            }}
          />
        </Box>
      </Box>
      <Box
        sx={{
          marginTop: '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        <Button
          maxWidth={{ xs: '100%', sm: '100%' }}
          width={'100%'}
          buttontype={'orange'}
          disabled={isSubmitting}
          onClick={() => handleSubmit()}
        >
          {isSubmitting ? (
            <SpinningBox />
          ) : collectPayment ? (
            t('checkout')
          ) : (
            t('signup')
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default Registration;
